import moment from 'moment';
import type { Moment } from 'moment';
import lodash from 'lodash';

/** A date range as a [start, end] pair of moments (inclusive on both ends). */
export type DateRange = [Moment, Moment];

/** Granularity used when formatting dates/ranges for display. */
type TimeFormat = 'hour' | 'day' | 'month' | 'year';

/**
 * Preset identifiers for the date-range picker.
 * Keys equal values except `emptyCompare` (null = no comparison range selected).
 */
export const dateRangeTypes = {
  yesterday: 'yesterday',
  last7days: 'last7days',
  last28days: 'last28days',
  beginMonth: 'beginMonth',
  beginYear: 'beginYear',
  ownPeriod: 'ownPeriod',
  periodBefore: 'periodBefore',
  periodYearBefore: 'periodYearBefore',
  emptyCompare: null,
} as const;

/** Short date display format for German UI. */
const dateFormatDe = 'DD.MM.YY';
/** Short date display format for English UI. */
const dateFormatEn = 'DD/MM/YY';
/** Month-granularity display format (locale-aware month name). */
const dateFormatMonthDe = 'MMMM YY';
/** Year-granularity display format. */
const dateFormatYearDe = 'YYYY';

const DateRangeUtil = {
  dateRangeTypes,
  /**
   * Formats a week range starting from a given date.
   * @param startDate - The start date of the week in 'YYYY-MM-DD' format.
   * @param locale - The locale to use for formatting.
   * @returns The formatted week range.
   */
  formatWeekRange(startDate: string, locale = 'de'): string {
    const start = moment(startDate);
    if (!start.isValid()) return this.getDateFormatted(startDate, 'day', locale);

    return this.getDateRangeFormatted({
      dateRange: [start, moment(start).add(6, 'days')],
      timeFormat: 'day',
      language: locale,
    });
  },
  /**
   * Wrap two moment-parseable values into a moment range pair.
   * @param dates - [start, end] raw values; nullish input yields null.
   */
  getRangeWithDates(dates: Array<string | Date | Moment> | null | undefined): DateRange | null {
    if (!dates) return null;
    return [moment(dates[0]), moment(dates[1])];
  },
  /** Range covering exactly yesterday (start == end, at start of day). */
  getRangeYesterday(): DateRange {
    return [moment().startOf('day').add(-1, 'days'), moment().startOf('day').add(-1, 'days')];
  },
  /**
   * Range of the last X full days, ending yesterday.
   * @param days - Number of days BEFORE yesterday to include (pass 6 for "last 7 days").
   */
  getRangeLastXDays(days: number): DateRange {
    days += 1;
    return [moment().startOf('day').add(days * -1, 'days'), moment().startOf('day').add(-1, 'days')];
  },
  /** Range from the 1st of the current month until today. */
  getRangeBeginMonth(): DateRange {
    return [moment().startOf('month'), moment().startOf('day')];
  },
  /** Range from Jan 1st of the current year until today. */
  getRangeBeginYear(): DateRange {
    return [moment().startOf('year'), moment().startOf('day')];
  },
  /**
   * The equally long period directly before the given range (for period comparison).
   * @param dateRange - Range to shift; not mutated (deep-cloned first).
   */
  getRangeSameBefore(dateRange: DateRange): DateRange {
    dateRange = lodash.cloneDeep(dateRange);
    const daysBetween = dateRange[1].diff(dateRange[0], 'days') + 1;
    const newDateRange: DateRange = [
      dateRange[0].add(daysBetween * -1, 'days'),
      dateRange[1].add(daysBetween * -1, 'days'),
    ];
    return newDateRange;
  },
  /**
   * The same period one year earlier (for year-over-year comparison).
   * @param dateRange - Range to shift; not mutated (deep-cloned first).
   */
  getRangeSamePrevYear(dateRange: DateRange): DateRange {
    dateRange = lodash.cloneDeep(dateRange);
    const newDateRange: DateRange = [
      dateRange[0].add(-1, 'year'),
      dateRange[1].add(-1, 'year'),
    ];
    return newDateRange;
  },
  /**
   * Format a range for display, localized and joined with an en-dash.
   * @param dateRange - [start, end] moments; returned unchanged (as `any`) when either end is missing.
   * @param timeFormat - 'month' for month-granularity labels, otherwise day granularity.
   * @param language - 'de' or 'en' — chooses the date format and moment locale.
   */
  getDateRangeFormatted({ dateRange, timeFormat = 'day', language = 'en' }: {
    dateRange: DateRange;
    timeFormat?: TimeFormat;
    language?: string;
  }): string {
    dateRange = lodash.cloneDeep(dateRange);

    // Cast: original JS returns the (incomplete) range array itself when an end is missing
    if (!dateRange[0] || !dateRange[1]) return dateRange as any;

    const dateFormat = language === 'de' ? dateFormatDe : dateFormatEn;

    switch (timeFormat) {
      case 'month':
        return `${dateRange[0]
          .locale(language)
          .format(dateFormatMonthDe)} – ${dateRange[1]
          .locale(language)
          .format(dateFormatMonthDe)}`;
      case 'day':
      default:
        return `${dateRange[0]
          .locale(language)
          .format(dateFormat)} – ${dateRange[1]
          .locale(language)
          .format(dateFormat)}`;
    }
  },
  /**
   * Format a single date for display at the given granularity.
   * @param date - moment-parseable value.
   * @param timeFormat - Granularity of the label ('hour' appends 'HH:00').
   * @param language - 'de' or 'en' — chooses the date format and moment locale.
   */
  getDateFormatted(date: string | Date | Moment, timeFormat: TimeFormat = 'day', language = 'en'): string {
    const momDate = moment(date);
    if (!momDate.isValid) return '–';

    const dateFormat = language === 'de' ? dateFormatDe : dateFormatEn;

    switch (timeFormat) {
      case 'hour':
        return momDate.locale(language).format(`${dateFormat} HH:00`);
      case 'year':
        return momDate.locale(language).format(dateFormatYearDe);
      case 'month':
        return momDate.locale(language).format(dateFormatMonthDe);
      case 'day':
      default:
        return momDate.locale(language).format(dateFormat);
    }
  },
  /** Whether both ranges cover exactly the same calendar days. */
  compareDateRanges(rangeA: DateRange, rangeB: DateRange): boolean {
    const DF = 'YYYY-MM-DD';
    if (
      rangeA[0].format(DF) === rangeB[0].format(DF) &&
      rangeA[1].format(DF) === rangeB[1].format(DF)
    ) {
      return true;
    }
    return false;
  },
  /**
   * Reverse-map concrete dates to a preset value.
   * @param dates - [start, end] raw values.
   * @returns A `dateRangeTypes` preset when the dates match one, otherwise the custom '<start>–<end>' string.
   */
  getRangeValueFromDates(dates: Array<string | Date | Moment>): string {
    // Non-null assertion: `dates` is expected to be a valid pair here (original JS would throw the same way)
    const range = this.getRangeWithDates(dates)!;

    if (this.compareDateRanges(range, this.getRangeYesterday())) {
      return dateRangeTypes.yesterday;
    }
    if (this.compareDateRanges(range, this.getRangeLastXDays(6))) {
      return dateRangeTypes.last7days;
    }
    if (this.compareDateRanges(range, this.getRangeLastXDays(27))) {
      return dateRangeTypes.last28days;
    }
    if (this.compareDateRanges(range, this.getRangeBeginMonth())) {
      return dateRangeTypes.beginMonth;
    }
    if (this.compareDateRanges(range, this.getRangeBeginYear())) {
      return dateRangeTypes.beginYear;
    }
    // own period
    return `${dates[0]}–${dates[1]}`;
  },
  /**
   * Resolve a preset value or custom '<start>–<end>' string back to concrete dates.
   * @returns The moment range; null for unknown values.
   */
  getRangeDatesFromValue(value: string): DateRange | null {
    let newDates: DateRange | null = null;
    // Individual dates from datepicker
    if (value.includes('–')) {
      const datesSplit = value.split('–');
      newDates = DateRangeUtil.getRangeWithDates(datesSplit);
    } else {
      switch (value) {
        case dateRangeTypes.yesterday:
          newDates = DateRangeUtil.getRangeYesterday();
          break;
        case dateRangeTypes.last7days:
          newDates = DateRangeUtil.getRangeLastXDays(6);
          break;
        case dateRangeTypes.last28days:
          newDates = DateRangeUtil.getRangeLastXDays(27);
          break;
        case dateRangeTypes.beginMonth:
          newDates = DateRangeUtil.getRangeBeginMonth();
          break;
        case dateRangeTypes.beginYear:
          newDates = DateRangeUtil.getRangeBeginYear();
          break;
      }
    }

    return newDates;
  },
  /**
   * Full-calendar-year range for the annual review.
   * @param year - 4-digit year as string/number; defaults to the current year.
   */
  getAnualReviewDateRange(year?: string | number): DateRange {
    if (!year) year = moment().format('YYYY');
    return [moment(`${year}-01-01`), moment(`${year}-12-31`)];
  },
  /** Format a range as 'DD.MM.YY – DD.MM.YY' (German short format, locale-independent). */
  getDatesFormatted(dateRange: DateRange): string {
    return `${dateRange[0].format('DD.MM.YY')} – ${dateRange[1].format(
      'DD.MM.YY'
    )}`;
  },
};

export default DateRangeUtil;
